import { useId } from 'react';
import { Label as BaseLabel } from '@/components/label';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { scaleLinear } from 'd3-scale';
import { ChartRoot } from './chart';

type D = {
  id: string;
  label: string;
};

type LabelAlignment = 'top' | 'bottom' | 'left' | 'right';

type ScatterplotProps<T extends D> = ComposableStyledComponentProps<HTMLDivElement> & {
  data: T[];
  hasLabels?: boolean;
  highlightedIds?: (T['id'] | { id: T['id'] })[];
  idsAlignment?: { [key: string]: LabelAlignment };
  getXValue: (d: T) => number;
  getYValue: (d: T) => number;
  topAxisLabel: string;
  bottomAxisLabel: string;
};

const POINT_RADIUS = 1.5;

export function Scatterplot<T extends D>(props: ScatterplotProps<T>) {
  const {
    data,
    highlightedIds,
    idsAlignment,
    hasLabels,
    getXValue,
    getYValue,
    topAxisLabel,
    bottomAxisLabel,
    css,
  } = props;

  const maskId = useId();
  const highlightedMaskId = useId();

  const x = scaleLinear().domain([0, 1]).range([0, 100]);
  const y = scaleLinear().domain([0, 1]).range([100, 0]);

  const getOpacity = (id: T['id']) => {
    if (highlightedIds) {
      if (highlightedIds?.includes(id)) return 1;
      return 0.4;
    }

    return 1;
  };

  let highlighted: T[] | undefined;
  if (highlightedIds) {
    highlighted = data.filter((d) => highlightedIds.includes(d.id));
  }

  return (
    <ChartRoot rootCss={{ verticalAlign: 'flex-end' }} css={css}>
      <Svg viewBox="0 0 100 100">
        <polygon points="100,0 100,100 0,100" fill="var(--color-tertiary)" />
        <line
          x1="0"
          y1="100"
          x2="100"
          y2="0"
          stroke="white"
          strokeWidth="1"
          strokeDasharray="2 2"
        />
      </Svg>

      <Underlay>
        <AxisLabel>{topAxisLabel}</AxisLabel>
        <BottomAxisLabel>{bottomAxisLabel}</BottomAxisLabel>
      </Underlay>

      <Svg viewBox="0 0 100 100">
        <defs>
          {/* Simulate strokes using a mask. Ensures that strokes will have background color */}
          <mask id={maskId}>
            {data.map((d) => (
              <circle
                key={'mask-' + d.id}
                r={POINT_RADIUS}
                cx={x(getXValue(d))}
                cy={y(getYValue(d))}
                fill="white"
                stroke="black"
                strokeWidth="0.3"
              />
            ))}
          </mask>

          {highlighted && (
            <mask id={highlightedMaskId}>
              {highlighted.map((d) => (
                <circle
                  key={'highlighted-mask-' + d.id}
                  r={POINT_RADIUS}
                  cx={x(getXValue(d))}
                  cy={y(getYValue(d))}
                  fill="white"
                  stroke="black"
                  strokeWidth="0.3"
                />
              ))}
            </mask>
          )}
        </defs>

        <g mask={`url(#${maskId})`}>
          {data.map((d) => (
            <Point
              key={d.id}
              r={POINT_RADIUS}
              cx={x(getXValue(d))}
              cy={y(getYValue(d))}
              css={{ opacity: getOpacity(d.id) }}
            />
          ))}
        </g>

        {highlighted && (
          <g mask={`url(#${highlightedMaskId})`}>
            {highlighted.map((d) => (
              <Point
                key={d.id}
                r={POINT_RADIUS}
                cx={x(getXValue(d))}
                cy={y(getYValue(d))}
                css={{ opacity: getOpacity(d.id) }}
              />
            ))}
          </g>
        )}
      </Svg>

      {highlighted && (highlighted.length === 1 || hasLabels) && (
        <Overlay>
          {highlighted.map((d) => {
            const xPercentage = x(getXValue(d));
            const yPercentage = y(getYValue(d));
            let alignment: LabelAlignment;

            if (idsAlignment && idsAlignment[d.id]) {
              alignment = idsAlignment[d.id];
            } else {
              alignment = xPercentage > 50 ? 'left' : 'right';
            }

            return (
              <Label
                key={d.id}
                css={{
                  top:
                    alignment === 'top'
                      ? yPercentage - POINT_RADIUS * 3 + '%'
                      : alignment === 'bottom'
                      ? yPercentage + POINT_RADIUS * 3 + '%'
                      : yPercentage + '%',
                  left:
                    alignment === 'top' || alignment === 'bottom'
                      ? xPercentage + '%'
                      : alignment === 'left'
                      ? 'auto'
                      : xPercentage + POINT_RADIUS * 2 + '%',
                  right:
                    alignment === 'left' ? 100 - (xPercentage - POINT_RADIUS * 2) + '%' : 'auto',
                }}
                alignment={alignment}
              >
                {d.label}
              </Label>
            );
          })}
        </Overlay>
      )}
    </ChartRoot>
  );
}

export const Point = styled('circle', {
  fill: 'var(--color-primary)',
  transition: 'opacity 200ms ease-in-out',
});

const Underlay = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
});

const AxisLabel = styled(BaseLabel, {
  position: 'absolute',
  left: '$3',
  top: '$3',
  maxWidth: '12em',
  lineHeight: '$2',
  fontSize: '$1',
  color: 'var(--color-text-light)',

  '@bp2': {
    fontSize: '$2',
  },
});

const BottomAxisLabel = styled(AxisLabel, {
  left: 'auto',
  top: 'auto',
  bottom: '$3',
  right: '$3',
});

const Svg = styled('svg', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

const Overlay = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 2,
});

export const Label = styled(BaseLabel, {
  position: 'absolute',
  paddingBlock: '$1',
  paddingInline: '$2',
  color: 'var(--color-text-light)',
  lineHeight: '$1',
  backgroundColor: 'var(--color-background)',
  borderRadius: '$space$5',
  variants: {
    alignment: {
      top: {
        transform: 'translateX(-50%) translateY(-50%)',
      },
      bottom: {
        transform: 'translateX(-50%) translateY(-50%)',
      },
      left: {
        transform: 'translateY(-50%)',
      },
      right: {
        transform: 'translateY(-50%)',
      },
    },
  },
});
